import React, { useEffect, useMemo, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import TwoFactorCodeInput from "../../_UI/twoFactorCodeInput";
import CryptoIcon from "../../_UI/cryptoIcon";

import {
    getDepositAll,
    getDepositAsset,
    getDepositCurrency,
    getDepositFee,
    getDepositNetwork,
    getTwoFactorOptions,
    openDeposit,
    setCabinetDepositOpenModalStatus
} from "../../../store/cabinet/action";

import depositIcon from "../../../theme/assets/svg/b_cabinet_deposit_icon.svg";
import percentIcon from "../../../theme/assets/svg/b_cabinet_deposit_percent_icon.svg";
import periodIcon from "../../../theme/assets/svg/b_cabinet_deposit_period_icon.svg";
import warningIcon from "../../../theme/assets/svg/b_cabinet_receive_warning_icon.svg";
import feeIcon from "../../../theme/assets/svg/b_cabinet_deposit_fee_icon.svg";

const CODE_LENGTH = 6;
const DEPOSIT_2FA_SCOPE = "deposit";

const DEPOSIT_STEPS = {
    OPEN: 1,
    CONFIRM: 2,
};

const formatAmount = (value, maxDigits = 8) => {
    if (value === null || value === undefined || value === "") return "—";

    const numberValue = Number(value);

    if (Number.isNaN(numberValue)) return "—";

    return numberValue.toLocaleString("en-US", {
        maximumFractionDigits: maxDigits,
    });
};

const normalizeAmountValue = (value) => {
    const normalizedValue = String(value || "")
        .replace(",", ".")
        .replace(/[^\d.]/g, "");

    const valueParts = normalizedValue.split(".");

    if (valueParts.length <= 2) {
        return normalizedValue;
    }

    return `${valueParts[0]}.${valueParts.slice(1).join("")}`;
};

const getAmountNumber = (value) => {
    const numberValue = Number(value);

    if (!value || Number.isNaN(numberValue)) return null;

    return numberValue;
};

const getFeeUsdValue = (fee) => {
    return (
        fee?.fee_usd ||
        fee?.fee_usd_decimal ||
        fee?.fee_in_usd ||
        fee?.amount_usd ||
        fee?.usd_amount ||
        null
    );
};

const getPeriodKey = (period) => {
    return period?.id ? String(period.id) : "";
};

const getPeriodLabel = (period, t) => {
    const days = Number(period?.days);

    if (!Number.isFinite(days)) return "—";

    return `${days} ${t("дней")}`;
};

const getTwoFactorScopes = (options) => {
    if (Array.isArray(options)) return options;
    if (Array.isArray(options?.scopes)) return options.scopes;

    return [];
};

const isTwoFactorScopeEnabled = (options, scope) => {
    if (!options?.enabled) return false;

    return getTwoFactorScopes(options).includes(scope);
};

const CabinetDepositOpenModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const modalStatus = useSelector(store => store.cabinet.cabinetDepositOpenModalStatus);
    const depositAll = useSelector(store => store.cabinet.depositAllStatus);

    const depositCurrency = useSelector(store => store.cabinet.depositCurrency);
    const depositNetwork = useSelector(store => store.cabinet.depositNetwork);
    const depositNetworkCurrencyId = useSelector(store => store.cabinet.depositNetworkCurrencyId);
    const depositAsset = useSelector(store => store.cabinet.depositAsset);
    const depositFee = useSelector(store => store.cabinet.depositFee);
    const depositFeeStarted = useSelector(store => store.cabinet.depositFeeStarted);

    const depositOpenStarted = useSelector(store => store.cabinet.depositOpenStarted);
    const depositOpenErr = useSelector(store => store.cabinet.depositOpenErr);

    const twoFactorOptions = useSelector(store => store.cabinet.twoFactorOptions);

    const [step, setStep] = useState(DEPOSIT_STEPS.OPEN);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [selectedPeriodId, setSelectedPeriodId] = useState("");
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [selectedNetwork, setSelectedNetwork] = useState(null);
    const [amount, setAmount] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [openedDropdown, setOpenedDropdown] = useState("");

    const depositPlans = useMemo(() => {
        return Array.isArray(depositAll) ? depositAll : [];
    }, [depositAll]);

    const selectedPlan = useMemo(() => {
        return depositPlans.find((plan) => plan.id === selectedPlanId) || depositPlans[0] || null;
    }, [depositPlans, selectedPlanId]);

    const selectedPlanPeriods = useMemo(() => {
        return Array.isArray(selectedPlan?.periods) ? selectedPlan.periods : [];
    }, [selectedPlan]);

    const selectedPeriod = useMemo(() => {
        return selectedPlanPeriods.find((period) => {
            return getPeriodKey(period) === selectedPeriodId;
        }) || selectedPlanPeriods[0] || null;
    }, [selectedPlanPeriods, selectedPeriodId]);

    const selectedPlanAssets = useMemo(() => {
        return Array.isArray(selectedPlan?.assets) ? selectedPlan.assets : [];
    }, [selectedPlan]);

    const selectedAsset = useMemo(() => {
        return selectedPlanAssets.find((asset) => {
            return (
                asset?.currency?.id === selectedCurrency?.id &&
                asset?.network?.id === selectedNetwork?.id
            );
        }) || null;
    }, [selectedPlanAssets, selectedCurrency, selectedNetwork]);

    const selectedBalance = useMemo(() => {
        return depositAsset?.balances?.find((balance) => {
            return (
                balance?.asset?.currency?.id === selectedCurrency?.id &&
                balance?.asset?.network?.id === selectedNetwork?.id
            );
        }) || null;
    }, [depositAsset, selectedCurrency, selectedNetwork]);

    const selectedCurrencyCode = selectedCurrency?.code || "—";
    const selectedNetworkName = selectedNetwork?.name || selectedNetwork?.code || "—";
    const selectedNetworkCode = selectedNetwork?.native_symbol || selectedNetwork?.code || "";

    const selectedAssetIconType =
        selectedAsset?.type ||
        selectedBalance?.asset?.type ||
        selectedNetwork?.type ||
        "";

    const selectedAssetNetworkCode =
        selectedAsset?.network?.code ||
        selectedBalance?.asset?.network?.code ||
        selectedNetwork?.code ||
        selectedNetwork?.native_symbol ||
        "";

    const isDepositNetworkActual = depositNetworkCurrencyId === selectedCurrency?.id;
    const actualDepositNetwork = isDepositNetworkActual ? depositNetwork : false;

    const selectedAnnualPercent = selectedPeriod?.annual_percent || "0";
    const selectedPeriodLabel = getPeriodLabel(selectedPeriod, t);

    const isDeposit2faRequired = isTwoFactorScopeEnabled(twoFactorOptions, DEPOSIT_2FA_SCOPE);

    const feeDecimal = depositFee?.fee_decimal || "";
    const feeNativeSymbol = depositFee?.native_symbol || selectedNetwork?.native_symbol || "";
    const feeUsdValue = getFeeUsdValue(depositFee);

    const hasFeeUsdValue =
        feeUsdValue !== null &&
        feeUsdValue !== undefined &&
        feeUsdValue !== "";

    const networkFeeLabel = feeDecimal
        ? `${formatAmount(feeDecimal, 8)} ${feeNativeSymbol}${hasFeeUsdValue ? ` (≈ $${formatAmount(feeUsdValue, 2)})` : ""}`
        : "—";

    const availableBalance = selectedBalance?.amount_decimal || selectedBalance?.amount || "0";
    const availableBalanceNumber = Number(availableBalance) || 0;

    const minAmount = selectedAsset?.min_amount ?? selectedPlan?.min_amount ?? null;
    const maxAmount = selectedAsset?.max_amount ?? selectedPlan?.max_amount ?? null;

    const amountNumber = getAmountNumber(amount);
    const minAmountNumber = getAmountNumber(minAmount);
    const maxAmountNumber = getAmountNumber(maxAmount);

    const hasCurrencyDropdown = depositCurrency?.length > 1;
    const hasNetworkDropdown = actualDepositNetwork?.length > 1;
    const hasPeriodDropdown = selectedPlanPeriods.length > 1;

    const amountError = useMemo(() => {
        if (!amount) return "";

        if (amountNumber === null) {
            return "Введите корректную сумму";
        }

        if (minAmountNumber !== null && amountNumber < minAmountNumber) {
            return "Сумма меньше минимального депозита";
        }

        if (maxAmountNumber !== null && amountNumber > maxAmountNumber) {
            return "Сумма больше максимального депозита";
        }

        if (amountNumber > availableBalanceNumber) {
            return "Сумма больше доступного баланса";
        }

        return "";
    }, [amount, amountNumber, minAmountNumber, maxAmountNumber, availableBalanceNumber]);

    const isAmountValid =
        !!amount &&
        amountNumber !== null &&
        !amountError;

    const isContinueDisabled =
        !selectedPlan ||
        !selectedPeriod ||
        !selectedCurrency?.id ||
        !selectedNetwork?.id ||
        !selectedAsset?.id ||
        !isAmountValid;

    const isConfirmDisabled =
        depositOpenStarted ||
        (isDeposit2faRequired && verificationCode.length !== CODE_LENGTH);

    const closeModal = () => {
        if (depositOpenStarted) return;

        dispatch(setCabinetDepositOpenModalStatus(false));
    };

    const dropdownToggleHandler = (dropdownName) => {
        setOpenedDropdown((prevState) => {
            return prevState === dropdownName ? "" : dropdownName;
        });
    };

    const periodDropdownToggleHandler = () => {
        if (!hasPeriodDropdown) return;

        dropdownToggleHandler("period");
    };

    const currencyDropdownToggleHandler = () => {
        if (!hasCurrencyDropdown) return;

        dropdownToggleHandler("currency");
    };

    const networkDropdownToggleHandler = () => {
        if (!hasNetworkDropdown) return;

        dropdownToggleHandler("network");
    };

    const periodSelectHandler = (period) => {
        setSelectedPeriodId(getPeriodKey(period));
        setOpenedDropdown("");
    };

    const currencySelectHandler = (currency) => {
        setSelectedCurrency(currency);
        setSelectedNetwork(null);
        setAmount("");
        setOpenedDropdown("");
    };

    const networkSelectHandler = (network) => {
        setSelectedNetwork(network);
        setAmount("");
        setOpenedDropdown("");
    };

    const amountChangeHandler = (event) => {
        setAmount(normalizeAmountValue(event.target.value));
    };

    const maxAmountHandler = () => {
        const maxValue = maxAmountNumber !== null
            ? Math.min(maxAmountNumber, availableBalanceNumber)
            : availableBalanceNumber;

        if (!maxValue) return;

        setAmount(String(maxValue));
    };

    const continueHandler = () => {
        if (isContinueDisabled) return;

        setVerificationCode("");
        setOpenedDropdown("");
        setStep(DEPOSIT_STEPS.CONFIRM);
    };

    const successHandler = () => {
        toast(t("Депозит успешно открыт"));
        closeModal();
    };

    const confirmHandler = () => {
        if (isConfirmDisabled) return;
        if (!selectedAsset?.id) return;
        if (!selectedPeriod?.id) return;

        const payload = {
            deposit_id: selectedPlan?.id,
            period_id: selectedPeriod.id,
            asset_id: selectedAsset.id,
            amount,
        };

        if (isDeposit2faRequired) {
            payload.code = verificationCode;
        }

        dispatch(openDeposit(payload, successHandler));
    };

    useEffect(() => {
        if (modalStatus) {
            dispatch(getDepositAll());
            dispatch(getDepositCurrency());
            dispatch(getTwoFactorOptions());
        }
    }, [dispatch, modalStatus]);

    useEffect(() => {
        if (!modalStatus) {
            setStep(DEPOSIT_STEPS.OPEN);
            setSelectedPlanId(null);
            setSelectedPeriodId("");
            setSelectedCurrency(null);
            setSelectedNetwork(null);
            setAmount("");
            setVerificationCode("");
            setOpenedDropdown("");
        }
    }, [modalStatus]);

    useEffect(() => {
        if (!modalStatus) return;
        if (!depositPlans.length) return;

        setSelectedPlanId((prevState) => {
            if (prevState) return prevState;

            return depositPlans[0]?.id || null;
        });
    }, [modalStatus, depositPlans]);

    useEffect(() => {
        if (!modalStatus) return;
        if (!selectedPlanPeriods.length) {
            setSelectedPeriodId("");
            return;
        }

        const isSelectedPeriodAvailable = selectedPlanPeriods.some((period) => {
            return getPeriodKey(period) === selectedPeriodId;
        });

        if (!isSelectedPeriodAvailable) {
            setSelectedPeriodId(getPeriodKey(selectedPlanPeriods[0]));
        }
    }, [modalStatus, selectedPlanPeriods, selectedPeriodId]);

    useEffect(() => {
        if (!modalStatus) return;

        if (!depositCurrency?.length) {
            setSelectedCurrency(null);
            return;
        }

        const isSelectedCurrencyAvailable = depositCurrency.some((currency) => {
            return currency?.id === selectedCurrency?.id;
        });

        if (!isSelectedCurrencyAvailable) {
            setSelectedCurrency(depositCurrency[0]);
        }
    }, [modalStatus, depositCurrency, selectedCurrency]);

    useEffect(() => {
        if (!modalStatus) return;
        if (!selectedCurrency?.id) return;

        dispatch(getDepositNetwork(selectedCurrency.id));
    }, [dispatch, modalStatus, selectedCurrency]);

    useEffect(() => {
        if (!modalStatus) return;
        if (!selectedCurrency?.id) return;
        if (depositNetworkCurrencyId !== selectedCurrency.id) return;

        if (!actualDepositNetwork?.length) {
            setSelectedNetwork(null);
            return;
        }

        const isSelectedNetworkAvailable = actualDepositNetwork.some((network) => {
            return network?.id === selectedNetwork?.id;
        });

        if (!isSelectedNetworkAvailable) {
            setSelectedNetwork(actualDepositNetwork[0]);
        }
    }, [modalStatus, actualDepositNetwork, depositNetworkCurrencyId, selectedCurrency, selectedNetwork]);

    useEffect(() => {
        if (!modalStatus) return;
        if (!selectedCurrency?.id || !selectedNetwork?.id) return;

        dispatch(getDepositAsset(selectedCurrency.id, selectedNetwork.id));
        dispatch(getDepositFee(selectedCurrency.id, selectedNetwork.id));
    }, [dispatch, modalStatus, selectedCurrency, selectedNetwork]);

    return (
        <Popup
            open={!!modalStatus}
            modal
            nested
            closeOnDocumentClick={!depositOpenStarted}
            onClose={closeModal}
            className="cabinetDepositOpenModal"
        >
            <div className={Styles.modalContainer}>
                <div
                    className={`
                        ${Styles.modalCloseBtn}
                        ${depositOpenStarted ? Styles.modalCloseBtnDisabled : ""}
                    `}
                    onClick={closeModal}
                >
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    {step === DEPOSIT_STEPS.OPEN && (
                        <>
                            <div className={Styles.titleRow}>
                                <div className={Styles.titleIcon}>
                                    <img src={depositIcon} alt={t("Открытие депозита")} />
                                </div>

                                <div className={Styles.titleTextWrap}>
                                    <div className={Styles.title}>
                                        {t("Открытие депозита")}
                                    </div>

                                    <div className={Styles.note}>
                                        {t("Сохраните и приумножьте ваши активы")}
                                    </div>
                                </div>
                            </div>

                            {selectedPlan ? (
                                <>
                                    <div className={Styles.formGrid}>
                                        <div className={Styles.formItem}>
                                            <div className={Styles.fieldLabel}>
                                                {t("Годовая процентная ставка")}
                                            </div>

                                            <div className={Styles.selectField}>
                                                <span className={Styles.selectIcon}>
                                                    <img src={percentIcon} alt="" />
                                                </span>

                                                <span className={Styles.selectValue}>
                                                    {formatAmount(selectedAnnualPercent)}% {t("годовых")}
                                                </span>
                                            </div>
                                        </div>

                                        <div className={Styles.formItem}>
                                            <div className={Styles.fieldLabel}>
                                                {t("Срок")}
                                            </div>

                                            <button
                                                type="button"
                                                className={Styles.selectField}
                                                onClick={periodDropdownToggleHandler}
                                            >
                                                <span className={Styles.selectIcon}>
                                                    <img src={periodIcon} alt="" />
                                                </span>

                                                <span className={Styles.selectValue}>
                                                    {selectedPeriodLabel}
                                                </span>

                                                {hasPeriodDropdown && (
                                                    <span className={Styles.selectArrow} />
                                                )}
                                            </button>

                                            {openedDropdown === "period" && hasPeriodDropdown && (
                                                <div className={Styles.dropdown}>
                                                    {selectedPlanPeriods.map((period) => (
                                                        <button
                                                            type="button"
                                                            key={period.id}
                                                            className={Styles.dropdownItem}
                                                            onClick={() => periodSelectHandler(period)}
                                                        >
                                                            {getPeriodLabel(period, t)}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className={Styles.formItem}>
                                            <div className={Styles.fieldLabel}>
                                                {t("Актив")}
                                            </div>

                                            <button
                                                type="button"
                                                className={Styles.selectField}
                                                onClick={currencyDropdownToggleHandler}
                                                disabled={!depositCurrency?.length}
                                            >
                                                <CryptoIcon
                                                    code={selectedCurrencyCode}
                                                    networkCode={selectedAssetNetworkCode}
                                                    assetType={selectedAssetIconType}
                                                    className={Styles.cryptoSelectIcon}
                                                />

                                                <span className={Styles.selectValue}>
                                                    {selectedCurrencyCode}
                                                </span>

                                                {hasCurrencyDropdown && (
                                                    <span className={Styles.selectArrow} />
                                                )}
                                            </button>

                                            {openedDropdown === "currency" && hasCurrencyDropdown && (
                                                <div className={Styles.dropdown}>
                                                    {depositCurrency.map((currency) => (
                                                        <button
                                                            type="button"
                                                            key={currency.id}
                                                            className={Styles.dropdownItem}
                                                            onClick={() => currencySelectHandler(currency)}
                                                        >
                                                            <CryptoIcon
                                                                code={currency?.code}
                                                                className={Styles.cryptoDropdownIcon}
                                                            />

                                                            <span>
                                                                {currency?.code || "—"}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className={Styles.formItem}>
                                            <div className={Styles.fieldLabel}>
                                                {t("Сеть")}
                                            </div>

                                            <button
                                                type="button"
                                                className={Styles.selectField}
                                                onClick={networkDropdownToggleHandler}
                                                disabled={!actualDepositNetwork?.length}
                                            >
                                                <CryptoIcon
                                                    code={selectedCurrencyCode}
                                                    networkCode={selectedNetwork?.code || selectedNetwork?.native_symbol}
                                                    assetType={selectedNetwork?.type || selectedAssetIconType}
                                                    className={Styles.cryptoSelectIcon}
                                                />

                                                <span className={Styles.selectValue}>
                                                    {selectedNetworkName}
                                                </span>

                                                {hasNetworkDropdown && (
                                                    <span className={Styles.selectArrow} />
                                                )}
                                            </button>

                                            {openedDropdown === "network" && hasNetworkDropdown && (
                                                <div className={Styles.dropdown}>
                                                    {actualDepositNetwork.map((network) => (
                                                        <button
                                                            type="button"
                                                            key={network.id}
                                                            className={Styles.dropdownItem}
                                                            onClick={() => networkSelectHandler(network)}
                                                        >
                                                            <CryptoIcon
                                                                code={selectedCurrencyCode}
                                                                networkCode={network?.code || network?.native_symbol}
                                                                assetType={network?.type}
                                                                className={Styles.cryptoDropdownIcon}
                                                            />

                                                            <span>
                                                                {network?.name || network?.code || "—"}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className={Styles.amountBlock}>
                                        <div className={Styles.amountTop}>
                                            <div className={Styles.fieldLabel}>
                                                {t("Сумма")}
                                            </div>

                                            <div className={Styles.availableText}>
                                                {t("Доступно")}: {formatAmount(availableBalance)} {selectedCurrencyCode}
                                            </div>
                                        </div>

                                        <div className={Styles.amountField}>
                                            <input
                                                type="text"
                                                value={amount}
                                                onChange={amountChangeHandler}
                                                placeholder="0"
                                                inputMode="decimal"
                                            />

                                            <div className={Styles.amountCurrency}>
                                                {selectedCurrencyCode}
                                            </div>

                                            <button
                                                type="button"
                                                className={Styles.maxBtn}
                                                onClick={maxAmountHandler}
                                                disabled={!availableBalanceNumber}
                                            >
                                                {t("MAX")}
                                            </button>
                                        </div>

                                        <div className={Styles.amountLimits}>
                                            {t("Мин. депозит")} {formatAmount(minAmount)} {selectedCurrencyCode} / {t("Макс. депозит")} {formatAmount(maxAmount)} {selectedCurrencyCode}
                                        </div>

                                        {amountError && (
                                            <div className={Styles.amountError}>
                                                {t(amountError)}
                                            </div>
                                        )}

                                        {!selectedAsset?.id && selectedCurrency?.id && selectedNetwork?.id && (
                                            <div className={Styles.amountError}>
                                                {t("Для выбранной валюты и сети депозит недоступен")}
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        className={Styles.continueBtn}
                                        onClick={continueHandler}
                                        disabled={isContinueDisabled}
                                    >
                                        {t("Продолжить")}
                                    </button>
                                </>
                            ) : (
                                <div className={Styles.emptyState}>
                                    {t("Доступные депозиты не найдены")}
                                </div>
                            )}
                        </>
                    )}

                    {step === DEPOSIT_STEPS.CONFIRM && (
                        <>
                            <div className={Styles.titleRow}>
                                <div className={Styles.titleIcon}>
                                    <img src={depositIcon} alt={t("Подтверждение депозита")} />
                                </div>

                                <div className={Styles.titleTextWrap}>
                                    <div className={Styles.title}>
                                        {t("Подтверждение депозита")}
                                    </div>

                                    {isDeposit2faRequired && (
                                        <div className={Styles.note}>
                                            {t("Введите 6-значный код из приложения-аутентификатора")}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className={Styles.confirmBox}>
                                <div className={Styles.confirmRow}>
                                    <span>{t("Годовая процентная ставка")}</span>
                                    <strong>{formatAmount(selectedAnnualPercent)}% {t("годовых")}</strong>
                                </div>

                                <div className={Styles.confirmRow}>
                                    <span>{t("Срок")}</span>
                                    <strong>{selectedPeriodLabel}</strong>
                                </div>

                                <div className={Styles.confirmRow}>
                                    <span>{t("Актив")}</span>
                                    <strong className={Styles.confirmValueWithIcon}>
                                        <CryptoIcon
                                            code={selectedCurrencyCode}
                                            networkCode={selectedAssetNetworkCode}
                                            assetType={selectedAssetIconType}
                                            className={Styles.cryptoConfirmIcon}
                                        />
                                        {selectedCurrencyCode}
                                    </strong>
                                </div>

                                <div className={Styles.confirmRow}>
                                    <span>{t("Сеть")}</span>
                                    <strong className={Styles.confirmValueWithIcon}>
                                        <CryptoIcon
                                            code={selectedCurrencyCode}
                                            networkCode={selectedNetwork?.code || selectedNetwork?.native_symbol}
                                            assetType={selectedNetwork?.type || selectedAssetIconType}
                                            className={Styles.cryptoConfirmIcon}
                                        />
                                        {selectedNetworkName}
                                    </strong>
                                </div>

                                <div className={Styles.confirmRow}>
                                    <span>{t("Итого")}</span>
                                    <strong>{formatAmount(amount)} {selectedCurrencyCode}</strong>
                                </div>

                                <div className={Styles.depositFeeBox}>
                                    <div className={Styles.depositFeeIcon}>
                                        <img src={feeIcon} alt="" />
                                    </div>

                                    <div className={Styles.depositFeeText}>
                                        <div className={Styles.depositFeeTitle}>
                                            {t("Комиссия сети")}
                                        </div>

                                        <div className={Styles.depositFeeValue}>
                                            {depositFeeStarted ? t("Загрузка") : networkFeeLabel}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {isDeposit2faRequired && (
                                <TwoFactorCodeInput
                                    value={verificationCode}
                                    onChange={setVerificationCode}
                                    formErr={depositOpenErr}
                                    errorFieldName="code"
                                    isLoading={depositOpenStarted}
                                    label="Код аутентификации"
                                    hint="Откройте Google Authenticator и введите код, который сейчас отображается для Kairo."
                                />
                            )}

                            <div className={Styles.confirmNotice}>
                                <div className={Styles.confirmNoticeIcon}>
                                    <img src={warningIcon} alt="" />
                                </div>

                                <div className={Styles.confirmNoticeText}>
                                    {t("Проверьте все данные перед подтверждением")}
                                </div>
                            </div>

                            <div className={Styles.actionsRow}>
                                <button
                                    type="button"
                                    className={Styles.cancelBtn}
                                    onClick={closeModal}
                                    disabled={depositOpenStarted}
                                >
                                    {t("Отмена")}
                                </button>

                                <button
                                    type="button"
                                    className={Styles.confirmBtn}
                                    onClick={confirmHandler}
                                    disabled={isConfirmDisabled}
                                >
                                    {depositOpenStarted ? t("Отправка") : t("Подтвердить и отправить")}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Popup>
    );
};

export default CabinetDepositOpenModal;
